/**
 * Panel „Statusverteilung" der Uebersicht - woertliche Portierung von `index.html:14805-14821`.
 *
 * ================= Woertlich uebernommene Eigenheiten =================
 *
 * 1. `progressPct` teilt die Summe der Stufen durch die Zahl der aktiven Bestellungen UND durch die
 *    feste 7 - die letzte Stufe des Zeitstrahls. Eine Board-Konfiguration mit einer anderen Zahl von
 *    Spalten aendert daran nichts, weil `setStatusDefinitions` die Stufen ohnehin auf 0..7 abbildet.
 * 2. Balken und Liste zeigen nur Status MIT Bestellungen (`filter(row => row.count)`), aber die
 *    Ersatzmeldung „Keine aktiven Bestellungen." haengt am `||` NACH `.join('')` - sie erscheint
 *    also genau dann, wenn KEIN Status uebrig bleibt.
 * 3. Der Balken rechnet `row.count / active.length * 100` und faellt bei `active.length === 0` auf
 *    0 zurueck - ein Zweig, der zusammen mit Eigenheit 2 nie sichtbar wird (ohne aktive
 *    Bestellungen ist auch jeder `count` 0 und die Liste leer).
 *
 * ================= BEFUND: Inline-Stile sind hier nicht signaturfaehig =================
 *
 * `.ops-pipeline-segment` traegt `style="width:9.090909090909092%;background:#8a63d2"`,
 * `.ops-status-row` traegt `style="--status-color:#8a63d2"`. Beide entstehen im Original als
 * ATTRIBUTTEXT (`innerHTML`), hier ueber die CSSOM - Chromium serialisiert das anders zurueck
 * (`width: 9.0909%; background: rgb(138, 99, 210);`). Derselbe Befund wie V3 im Bericht zu Task 9,
 * hier zusaetzlich mit FARB-Normalisierung (`#8a63d2` -> `rgb(...)`) und der Kuerzung auf sechs
 * signifikante Stellen. Der Struktur-Bildvergleich kann diese beiden Knoten deshalb nicht treffen,
 * solange `test/visual/signatur.ts` das `stil`-Feld nicht ueber einen CSSOM-Rundlauf normalisiert.
 * Gemeldet, nicht repariert - die Rechnung selbst ist im Komponententest belegt.
 */
import { NUM } from "../../lib/format.ts";
import type { StatusDefinition } from "../../lib/status.ts";

/** Eine Zeile aus `statusCounts` (index.html:14641-14644). */
export interface Statuszeile {
  status: StatusDefinition;
  count: number;
}

export interface StatusverteilungProps {
  zeilen: readonly Statuszeile[];
  /** `active.length` - Nenner der Balkenbreite. */
  aktiveGesamt: number;
  /** `progressPct`. */
  fortschritt: number;
}

export function Statusverteilung({ zeilen, aktiveGesamt, fortschritt }: StatusverteilungProps) {
  const belegte = zeilen.filter((row) => row.count);
  return (
    <aside className="ops-panel ops-status-panel">
      <div className="ops-panel-head">
        <div className="ops-panel-title">
          <h2>Statusverteilung</h2>
          <p>Fortschritt der aktiven Bestellungen</p>
        </div>
      </div>
      <div className="ops-pipeline">
        <div className="ops-progress-copy">
          {/*
            EIN Textknoten, nicht zwei. `<strong>{fortschritt}%</strong>` erzeugt in React die
            Knoten "52" und "%"; das Original hat an dieser Stelle einen einzigen Textknoten "52%".
            `domSignatur` erfasst die direkten Textknoten EINZELN und verbindet sie mit einem
            Leerzeichen - aus zwei Knoten wird dort "52 %", ein echter Unterschied im
            Struktur-Vergleich. `textContent` (und damit jeder Komponententest, der darueber prueft)
            sieht ihn NICHT, weil es ohne Trenner verkettet. Gefunden hat ihn erst
            `uebersichtStruktur.test.tsx` gegen die Baseline.
          */}
          <strong>{`${fortschritt}%`}</strong>
          <span>Ø Prozessfortschritt</span>
        </div>
        <div className="ops-pipeline-bar">
          {belegte.map((row) => (
            <span
              key={row.status.id}
              className="ops-pipeline-segment"
              style={{
                width: `${aktiveGesamt ? (row.count / aktiveGesamt) * 100 : 0}%`,
                background: row.status.color,
              }}
              title={`${row.status.label}: ${NUM.format(row.count)}`}
            ></span>
          ))}
        </div>
        <div className="ops-status-list">
          {belegte.length ? (
            belegte.map((row) => (
              <div
                key={row.status.id}
                className="ops-status-row"
                style={{ "--status-color": row.status.color } as React.CSSProperties}
              >
                <span className="ops-status-dot"></span>
                <span>{row.status.label}</span>
                <b>{NUM.format(row.count)}</b>
              </div>
            ))
          ) : (
            <div className="ops-empty">Keine aktiven Bestellungen.</div>
          )}
        </div>
      </div>
    </aside>
  );
}
